import { FEATURES } from '@constants/Text'
import { lazyLoadView } from '@utils/LazyLoad'
import { SUBTITULO_ROTA } from '@modules/ai-framework/constants/copy'
import { retornoDoCheckoutGuard } from '@modules/ai-framework/utils/retorno-do-checkout-guard'

// Molde: src/modules/aawz-intelligence-v2/routes.js:4-15 (guard global em
// src/router/index.js:255-256 chama meta.customPermission({ user, show })).
export const checkPermission = function(opts) {
  const show = opts.show || {}
  return Boolean(show[FEATURES.AI_FRAMEWORK])
}

// `meta` precisa ser repetido em cada child: o guard do router lê `routeTo.meta`, que
// é o meta da rota MATCHED (a folha), não o do pai — sem isto o acesso direto por URL
// a /central-ia/uso passaria sem o gate da feature.
const META_MODULO = { customPermission: checkPermission }
// O subtítulo do cabeçalho do módulo é por rota (`meta.subtitulo`): a nav-bar do shell
// lê `$route.meta.subtitulo` e passa ao `NavBarNavigation`. Sempre junto do gate.
const metaCom = (subtitulo) => ({ ...META_MODULO, subtitulo })

// Estrutura de shell (patterns.md → "Module nav-bar shell"): `/central-ia` monta
// `ai-framework-nav-bar.vue`, que renderiza a sub-navegação uma única vez e o
// `router-view` das 7 telas. Até o PR #1686 as rotas eram de TOPO e cada view
// renderizava seu próprio NavBarNavigation — o que reprovava o gate
// `design-system:no-regression` nas 7 e duplicava `navRoutes` em 7 arquivos.
// `/central-ia` continua sem tela própria: redireciona para o catálogo.
export default [
  {
    path: '/central-ia',
    component: function() {
      return lazyLoadView(
        import('@modules/ai-framework/views/nav-bar/ai-framework-nav-bar.vue')
      )
    },
    meta: META_MODULO,
    children: [
      {
        path: '',
        name: 'aiFramework',
        redirect: { name: 'aiFrameworkCatalog' },
        meta: META_MODULO,
      },
      {
        path: 'catalogo',
        name: 'aiFrameworkCatalog',
        meta: metaCom(SUBTITULO_ROTA.CATALOGO),
        component: function() {
          return lazyLoadView(
            import('@modules/ai-framework/views/catalog.vue')
          )
        },
      },
      {
        path: 'catalogo/:slug',
        name: 'aiFrameworkAgentDetail',
        props: true,
        meta: metaCom(SUBTITULO_ROTA.AGENTE),
        component: function() {
          return lazyLoadView(
            import('@modules/ai-framework/views/agent-detail.vue')
          )
        },
      },
      {
        path: 'meus-agentes',
        name: 'aiFrameworkMyAgents',
        meta: metaCom(SUBTITULO_ROTA.MEUS_AGENTES),
        component: function() {
          return lazyLoadView(
            import('@modules/ai-framework/views/my-agents.vue')
          )
        },
      },
      // spec-003 (Conversar). Nome camelCase p/ casar com a sub-navegação.
      {
        path: 'conversar',
        name: 'aiFrameworkConversar',
        meta: metaCom(SUBTITULO_ROTA.CONVERSAR),
        component: function() {
          return lazyLoadView(
            import('@modules/ai-framework/views/agent-chat.vue')
          )
        },
      },
      // spec-001 project-ia-payment (US-1) — a "área de créditos" que a user story
      // manda abrir. Name em camelCase como as três acima: o kebab das rotas de
      // baixo veio do contrato §1 da spec-004, que não vale para esta.
      //
      // ★ As abas Planos · Adicionar créditos · Uso são ROTAS FILHAS (endereçáveis pela
      // URL; é o que deixa o header da nav-bar mudar por rota via `ConditionalTeleport`).
      // Elas NÃO têm componente próprio: a tela é uma só (`creditos.vue`, no pai) e troca
      // o conteúdo por `$route.name` — o estado das abas (seleções, erros de checkout) é
      // o mesmo do modal de créditos, pelo `creditosAbasMixin`, e três views só o
      // reembrulhariam. O pai redireciona para Planos; a query (`?checkout=cancelado`)
      // sobrevive ao redirect. O `meta` está repetido em CADA filha: o guard lê o da
      // rota MATCHED (a folha), não o do pai.
      //
      // ★ `beforeEnter` = retorno da Stripe (`utils/retorno-do-checkout-guard.js`): quem
      // saiu para o checkout pelo modal, de outra rota, volta para ela com o modal
      // reaberto — antes de esta view montar. Guard de ROTA, e não `beforeRouteEnter`:
      // `lazyLoadView` não expõe guards de componente. Fica no pai porque ele é ativado
      // junto com a filha na carga completa da página, que é como a Stripe devolve.
      {
        path: 'creditos',
        name: 'aiFrameworkCreditos',
        meta: META_MODULO,
        redirect: { name: 'aiFrameworkCreditosPlanos' },
        beforeEnter: retornoDoCheckoutGuard,
        component: function() {
          return lazyLoadView(
            import('@modules/ai-framework/views/creditos.vue')
          )
        },
        children: [
          {
            path: 'planos',
            name: 'aiFrameworkCreditosPlanos',
            meta: metaCom(SUBTITULO_ROTA.CREDITOS_PLANOS),
          },
          {
            path: 'adicionar',
            name: 'aiFrameworkCreditosAdicionar',
            meta: metaCom(SUBTITULO_ROTA.CREDITOS_ADICIONAR),
          },
          {
            path: 'uso',
            name: 'aiFrameworkCreditosUso',
            meta: metaCom(SUBTITULO_ROTA.CREDITOS_USO),
          },
        ],
      },
      // spec-002 project-ia-payment (US-2) — tela de RETORNO do checkout. É rota-FILHA de
      // `creditos` para herdar o shell da Central de IA, e o id do pagamento vai na URL
      // (`props: true`): o consultor volta do domínio da Stripe, onde nada do estado de
      // sessão sobreviveu, e o refresh precisa reabrir o mesmo pagamento.
      // ★ `meta` repetido AQUI, na folha: o guard global lê `routeTo.meta`, que é o meta da
      // rota MATCHED. Sem esta linha, a URL direta passa por cima do gate da feature.
      // O mesmo `beforeEnter` de `creditos`: a compra avulsa feita pelo modal volta para
      // a rota de origem, e o estado do pagamento é mostrado dentro do modal.
      {
        path: 'creditos/pagamento/:idPagamento',
        name: 'aiFrameworkCreditosPagamento',
        props: true,
        meta: metaCom(SUBTITULO_ROTA.CREDITOS_PAGAMENTO),
        beforeEnter: retornoDoCheckoutGuard,
        component: function() {
          return lazyLoadView(
            import('@modules/ai-framework/views/creditos-pagamento.vue')
          )
        },
      },
      // spec-004 (Auditoria & Aprovações) — names em kebab (byte-idênticos ao
      // contrato ui-components §1). O gate adicional `gestor` (Aprovações/Uso) é
      // aplicado DENTRO da view (icon-no-module-access para não-gestor por URL
      // direta) — as abas nem aparecem pra ele.
      {
        path: 'aprovacoes',
        name: 'ai-framework-aprovacoes',
        meta: metaCom(SUBTITULO_ROTA.APROVACOES),
        component: function() {
          return lazyLoadView(
            import('@modules/ai-framework/views/approvals.vue')
          )
        },
      },
      {
        path: 'execucoes',
        name: 'ai-framework-execucoes',
        meta: metaCom(SUBTITULO_ROTA.EXECUCOES),
        component: function() {
          return lazyLoadView(
            import('@modules/ai-framework/views/executions.vue')
          )
        },
      },
      {
        path: 'uso',
        name: 'ai-framework-uso',
        meta: metaCom(SUBTITULO_ROTA.USO),
        component: function() {
          return lazyLoadView(
            import('@modules/ai-framework/views/usage.vue')
          )
        },
      },
    ],
  },
]
